import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@mui/material';
import {
  Home,
  ContentCopy,
  Check,
  ExpandMore,
  Code,
  Security,
  IntegrationInstructions,
  Api,
  WebhookOutlined,
  Key,
  CloudUpload,
  Chat,
  SmartToy,
  People,
  Assessment,
  ArrowBack,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`api-tabpanel-${index}`}
      aria-labelledby={`api-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface CodeBlockProps {
  code: string;
  language?: string;
}

const CodeBlock: React.FC<CodeBlockProps> = ({ code, language = 'json' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      // Try modern clipboard API first (requires HTTPS)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback for non-HTTPS contexts
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand('copy');
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          console.error('Fallback: Could not copy text', err);
        }

        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Box sx={{ position: 'relative', my: 2 }}>
      <Paper
        sx={{
          p: 2,
          bgcolor: '#1e1e1e',
          color: '#d4d4d4',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          overflow: 'auto',
          maxHeight: '400px',
        }}
      >
        <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
          <IconButton
            onClick={handleCopy}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            }}
          >
            {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
          </IconButton>
        </Tooltip>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {code}
        </pre>
      </Paper>
    </Box>
  );
};

interface EndpointCardProps {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  endpoint: string;
  description: string;
  requestExample?: string;
  responseExample?: string;
  parameters?: Array<{ name: string; type: string; required: boolean; description: string }>;
  headers?: Array<{ name: string; value: string; description: string }>;
}

const EndpointCard: React.FC<EndpointCardProps> = ({
  method,
  endpoint,
  description,
  requestExample,
  responseExample,
  parameters,
  headers,
}) => {
  const methodColors = {
    GET: '#61affe',
    POST: '#49cc90',
    PUT: '#fca130',
    PATCH: '#50e3c2',
    DELETE: '#f93e3e',
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Chip
            label={method}
            sx={{
              bgcolor: methodColors[method],
              color: 'white',
              fontWeight: 'bold',
              minWidth: 80,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 'bold',
              wordBreak: 'break-all',
            }}
          >
            {endpoint}
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 2 }}>
          {description}
        </Typography>

        {headers && headers.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Headers
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Value</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {headers.map((header, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{header.name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {header.value}
                      </TableCell>
                      <TableCell>{header.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {parameters && parameters.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Parameters
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Required</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parameters.map((param, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{param.name}</TableCell>
                      <TableCell>
                        <Chip label={param.type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {param.required ? (
                          <Chip label="Yes" size="small" color="error" />
                        ) : (
                          <Chip label="No" size="small" />
                        )}
                      </TableCell>
                      <TableCell>{param.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {requestExample && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Request Example
            </Typography>
            <CodeBlock code={requestExample} language="bash" />
          </Box>
        )}

        {responseExample && (
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Response Example
            </Typography>
            <CodeBlock code={responseExample} language="json" />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const APIDocumentationPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard/settings')}
          sx={{ textTransform: 'none' }}
        >
          Back to Settings
        </Button>
      </Box>

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <MuiLink
          color="inherit"
          onClick={() => navigate('/dashboard')}
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: 'pointer' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </MuiLink>
        <Typography color="text.primary">API Documentation</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom fontWeight="bold">
          <Api sx={{ fontSize: 40, mr: 1, verticalAlign: 'middle' }} />
          API Documentation
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Complete guide to integrating Converse AI Platform with your applications
        </Typography>
      </Box>

      {/* Quick Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Base URL:</strong> <code>https://api.converse.ai/v1</code> (Production) | <code>http://localhost:8001/api/v1</code> (Development)
      </Alert>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab icon={<Key />} iconPosition="start" label="Authentication" />
          <Tab icon={<Chat />} iconPosition="start" label="Conversations" />
          <Tab icon={<SmartToy />} iconPosition="start" label="AI Features" />
          <Tab icon={<People />} iconPosition="start" label="Contacts & Teams" />
          <Tab icon={<WebhookOutlined />} iconPosition="start" label="Webhooks" />
          <Tab icon={<CloudUpload />} iconPosition="start" label="File Uploads" />
          <Tab icon={<Assessment />} iconPosition="start" label="Reports & Analytics" />
          <Tab icon={<Code />} iconPosition="start" label="Inboxes & Channels" />
          <Tab icon={<Security />} iconPosition="start" label="Labels & Automation" />
          <Tab icon={<IntegrationInstructions />} iconPosition="start" label="SDKs" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={selectedTab} index={0}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Authentication
        </Typography>
        <Typography variant="body1" paragraph>
          Converse AI Platform uses JWT (JSON Web Tokens) for authentication. All API requests must include a valid access token in the Authorization header.
        </Typography>

        <EndpointCard
          method="POST"
          endpoint="/api/v1/auth/login"
          description="Authenticate user and receive access token"
          headers={[
            { name: 'Content-Type', value: 'application/json', description: 'Request body format' },
          ]}
          parameters={[
            { name: 'email', type: 'string', required: true, description: 'User email address' },
            { name: 'password', type: 'string', required: true, description: 'User password' },
          ]}
          requestExample={`curl -X POST http://localhost:8001/api/v1/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@example.com",
    "password": "your_password"
  }'`}
          responseExample={`{
  "access_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "refresh_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "token_type": "bearer",
  "expires_in": 1800,
  "user": {
    "id": 1,
    "email": "user@example.com",
    "name": "John Doe",
    "role": "agent"
  }
}`}
        />

        <EndpointCard
          method="POST"
          endpoint="/api/v1/auth/refresh"
          description="Refresh access token using refresh token"
          headers={[
            { name: 'Content-Type', value: 'application/json', description: 'Request body format' },
          ]}
          parameters={[
            { name: 'refresh_token', type: 'string', required: true, description: 'Valid refresh token' },
          ]}
          requestExample={`curl -X POST http://localhost:8001/api/v1/auth/refresh \\
  -H "Content-Type: application/json" \\
  -d '{
    "refresh_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
  }'`}
          responseExample={`{
  "access_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "token_type": "bearer",
  "expires_in": 1800
}`}
        />

        <Alert severity="warning" sx={{ mt: 3 }}>
          <strong>Important:</strong> Store your access token securely. Include it in the Authorization header for all authenticated requests:
          <CodeBlock code='Authorization: Bearer YOUR_ACCESS_TOKEN' language="text" />
        </Alert>
      </TabPanel>

      <TabPanel value={selectedTab} index={1}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Conversations API
        </Typography>
        <Typography variant="body1" paragraph>
          Manage customer conversations, messages, and interactions.
        </Typography>

        <EndpointCard
          method="GET"
          endpoint="/api/v1/conversations"
          description="Get list of conversations with pagination and filtering"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
          ]}
          parameters={[
            { name: 'page', type: 'integer', required: false, description: 'Page number (default: 1)' },
            { name: 'per_page', type: 'integer', required: false, description: 'Items per page (default: 20)' },
            { name: 'status', type: 'string', required: false, description: 'Filter by status: open, resolved, pending' },
            { name: 'inbox_id', type: 'integer', required: false, description: 'Filter by inbox ID' },
          ]}
          requestExample={`curl -X GET "http://localhost:8001/api/v1/conversations?page=1&per_page=20&status=open" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`}
          responseExample={`{
  "data": [
    {
      "id": 1,
      "inbox_id": 1,
      "contact_id": 5,
      "status": "open",
      "assignee_id": 2,
      "created_at": "2025-11-22T10:30:00Z",
      "updated_at": "2025-11-22T11:45:00Z",
      "messages_count": 12,
      "unread_count": 3
    }
  ],
  "meta": {
    "current_page": 1,
    "per_page": 20,
    "total": 156,
    "total_pages": 8
  }
}`}
        />

        <EndpointCard
          method="POST"
          endpoint="/api/v1/conversations/{id}/messages"
          description="Send a new message in a conversation"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
            { name: 'Content-Type', value: 'application/json', description: 'Request body format' },
          ]}
          parameters={[
            { name: 'content', type: 'string', required: true, description: 'Message content' },
            { name: 'message_type', type: 'string', required: false, description: 'Type: outgoing, incoming (default: outgoing)' },
            { name: 'private', type: 'boolean', required: false, description: 'Internal note (default: false)' },
          ]}
          requestExample={`curl -X POST http://localhost:8001/api/v1/conversations/1/messages \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "content": "Thank you for contacting us!",
    "message_type": "outgoing",
    "private": false
  }'`}
          responseExample={`{
  "id": 45,
  "conversation_id": 1,
  "content": "Thank you for contacting us!",
  "message_type": "outgoing",
  "sender_id": 2,
  "sender_name": "Agent Smith",
  "created_at": "2025-11-22T12:00:00Z",
  "private": false
}`}
        />
      </TabPanel>

      <TabPanel value={selectedTab} index={2}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          AI Features API
        </Typography>
        <Typography variant="body1" paragraph>
          Access AI-powered features including auto-responses, summaries, and sentiment analysis.
        </Typography>

        <EndpointCard
          method="POST"
          endpoint="/api/v1/ai/generate-response"
          description="Generate AI-powered response for a conversation"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
            { name: 'Content-Type', value: 'application/json', description: 'Request body format' },
          ]}
          parameters={[
            { name: 'conversation_id', type: 'integer', required: true, description: 'Conversation ID' },
            { name: 'context', type: 'string', required: false, description: 'Additional context for AI' },
            { name: 'tone', type: 'string', required: false, description: 'Response tone: professional, friendly, casual' },
          ]}
          requestExample={`curl -X POST http://localhost:8001/api/v1/ai/generate-response \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "conversation_id": 1,
    "tone": "professional",
    "context": "Customer asking about pricing"
  }'`}
          responseExample={`{
  "response": "Thank you for your interest in our pricing. We offer flexible plans starting at $29/month...",
  "confidence": 0.95,
  "suggestions": [
    "Include pricing page link",
    "Offer demo call"
  ]
}`}
        />

        <EndpointCard
          method="POST"
          endpoint="/api/v1/ai/summarize"
          description="Generate summary of a conversation"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
            { name: 'Content-Type', value: 'application/json', description: 'Request body format' },
          ]}
          parameters={[
            { name: 'conversation_id', type: 'integer', required: true, description: 'Conversation ID' },
            { name: 'length', type: 'string', required: false, description: 'Summary length: short, medium, detailed' },
          ]}
          requestExample={`curl -X POST http://localhost:8001/api/v1/ai/summarize \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "conversation_id": 1,
    "length": "medium"
  }'`}
          responseExample={`{
  "summary": "Customer inquired about enterprise pricing and requested a demo. Discussed features including AI auto-responses and analytics.",
  "key_points": [
    "Pricing inquiry",
    "Demo requested",
    "Interest in AI features"
  ],
  "sentiment": "positive",
  "action_items": [
    "Schedule demo call",
    "Send enterprise pricing sheet"
  ]
}`}
        />
      </TabPanel>

      <TabPanel value={selectedTab} index={3}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Contacts & Teams API
        </Typography>
        <Typography variant="body1" paragraph>
          Manage contacts, team members, and user assignments.
        </Typography>

        <EndpointCard
          method="POST"
          endpoint="/api/v1/contacts"
          description="Create a new contact"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
            { name: 'Content-Type', value: 'application/json', description: 'Request body format' },
          ]}
          parameters={[
            { name: 'name', type: 'string', required: true, description: 'Contact name' },
            { name: 'email', type: 'string', required: false, description: 'Contact email' },
            { name: 'phone', type: 'string', required: false, description: 'Contact phone number' },
            { name: 'custom_attributes', type: 'object', required: false, description: 'Custom contact attributes' },
          ]}
          requestExample={`curl -X POST http://localhost:8001/api/v1/contacts \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "John Doe",
    "email": "john@example.com",
    "phone": "+1234567890",
    "custom_attributes": {
      "company": "Acme Corp",
      "plan": "enterprise"
    }
  }'`}
          responseExample={`{
  "id": 42,
  "name": "John Doe",
  "email": "john@example.com",
  "phone": "+1234567890",
  "custom_attributes": {
    "company": "Acme Corp",
    "plan": "enterprise"
  },
  "created_at": "2025-11-22T12:30:00Z",
  "conversations_count": 0
}`}
        />

        <EndpointCard
          method="GET"
          endpoint="/api/v1/teams/{team_id}/members"
          description="Get team members"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
          ]}
          requestExample={`curl -X GET http://localhost:8001/api/v1/teams/1/members \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`}
          responseExample={`{
  "data": [
    {
      "id": 2,
      "name": "Agent Smith",
      "email": "agent@example.com",
      "role": "agent",
      "availability_status": "online",
      "assigned_conversations": 5
    }
  ]
}`}
        />
      </TabPanel>

      <TabPanel value={selectedTab} index={4}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Webhooks
        </Typography>
        <Typography variant="body1" paragraph>
          Configure webhooks to receive real-time notifications about events in your Converse account.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Webhooks allow you to receive HTTP POST requests to your server whenever specific events occur. Configure them in your account settings.
        </Alert>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight="bold">Available Events</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              <ListItem>
                <ListItemText
                  primary={<code>conversation.created</code>}
                  secondary="Triggered when a new conversation is created"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={<code>message.created</code>}
                  secondary="Triggered when a new message is sent or received"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={<code>conversation.status_changed</code>}
                  secondary="Triggered when conversation status changes"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={<code>contact.created</code>}
                  secondary="Triggered when a new contact is created"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={<code>contact.updated</code>}
                  secondary="Triggered when contact details are updated"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }} fontWeight="bold">
          Webhook Payload Example
        </Typography>
        <CodeBlock
          code={`{
  "event": "message.created",
  "timestamp": "2025-11-22T12:00:00Z",
  "account_id": 1,
  "data": {
    "message": {
      "id": 45,
      "conversation_id": 1,
      "content": "Hello, I need help!",
      "message_type": "incoming",
      "sender_id": 5,
      "created_at": "2025-11-22T12:00:00Z"
    },
    "conversation": {
      "id": 1,
      "status": "open",
      "inbox_id": 1
    }
  }
}`}
        />

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }} fontWeight="bold">
          Verifying Webhook Signatures
        </Typography>
        <Typography variant="body1" paragraph>
          Each webhook includes an <code>X-Converse-Signature</code> header with HMAC SHA256 signature. Verify it using your webhook secret:
        </Typography>
        <CodeBlock
          code={`import hmac
import hashlib

def verify_webhook(payload, signature, secret):
    expected = hmac.new(
        secret.encode(),
        payload.encode(),
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(signature, expected)`}
          language="python"
        />
      </TabPanel>

      <TabPanel value={selectedTab} index={5}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          File Uploads
        </Typography>
        <Typography variant="body1" paragraph>
          Upload and manage files, attachments, and media in conversations.
        </Typography>

        <EndpointCard
          method="POST"
          endpoint="/api/v1/conversations/{id}/attachments"
          description="Upload file attachment to a conversation"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
            { name: 'Content-Type', value: 'multipart/form-data', description: 'Multipart form data' },
          ]}
          parameters={[
            { name: 'file', type: 'file', required: true, description: 'File to upload (max 10MB)' },
            { name: 'message', type: 'string', required: false, description: 'Optional message with the file' },
          ]}
          requestExample={`curl -X POST http://localhost:8001/api/v1/conversations/1/attachments \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -F "file=@/path/to/document.pdf" \\
  -F "message=Here is the requested document"`}
          responseExample={`{
  "id": 12,
  "conversation_id": 1,
  "file_name": "document.pdf",
  "file_type": "application/pdf",
  "file_size": 245678,
  "file_url": "https://storage.converse.ai/files/abc123.pdf",
  "created_at": "2025-11-22T12:45:00Z"
}`}
        />

        <Alert severity="warning" sx={{ mt: 3 }}>
          <strong>Supported Formats:</strong> Images (JPG, PNG, GIF), Documents (PDF, DOC, DOCX), Archives (ZIP), Videos (MP4, MOV)
          <br />
          <strong>Maximum File Size:</strong> 10 MB per file
        </Alert>
      </TabPanel>

      <TabPanel value={selectedTab} index={6}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Reports & Analytics API
        </Typography>
        <Typography variant="body1" paragraph>
          Access comprehensive reports and analytics data for conversations, agents, and customer satisfaction.
        </Typography>

        <EndpointCard
          method="GET"
          endpoint="/api/v1/reports/conversations"
          description="Get conversation metrics and statistics for a date range"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
          ]}
          parameters={[
            { name: 'start_date', type: 'date', required: true, description: 'Start date (YYYY-MM-DD)' },
            { name: 'end_date', type: 'date', required: true, description: 'End date (YYYY-MM-DD)' },
            { name: 'inbox_id', type: 'integer', required: false, description: 'Filter by specific inbox' },
            { name: 'agent_id', type: 'integer', required: false, description: 'Filter by specific agent' },
          ]}
          requestExample={`curl -X GET "http://localhost:8001/api/v1/reports/conversations?start_date=2025-11-01&end_date=2025-11-25" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`}
          responseExample={`{
  "total_conversations": 450,
  "resolved_conversations": 380,
  "open_conversations": 70,
  "average_resolution_time_hours": 2.5,
  "average_first_response_time_minutes": 8.3,
  "conversations_by_status": {
    "open": 70,
    "resolved": 380,
    "pending": 25
  },
  "conversations_by_inbox": [
    {"inbox_id": 1, "inbox_name": "Website Chat", "count": 250},
    {"inbox_id": 2, "inbox_name": "Email Support", "count": 200}
  ]
}`}
        />

        <EndpointCard
          method="GET"
          endpoint="/api/v1/reports/agents"
          description="Get agent performance metrics including response times and conversation counts"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
          ]}
          parameters={[
            { name: 'start_date', type: 'date', required: true, description: 'Start date (YYYY-MM-DD)' },
            { name: 'end_date', type: 'date', required: true, description: 'End date (YYYY-MM-DD)' },
          ]}
          requestExample={`curl -X GET "http://localhost:8001/api/v1/reports/agents?start_date=2025-11-01&end_date=2025-11-25" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`}
          responseExample={`{
  "agents": [
    {
      "agent_id": 2,
      "agent_name": "John Doe",
      "total_conversations": 85,
      "resolved_conversations": 75,
      "average_response_time_minutes": 5.2,
      "customer_satisfaction_score": 4.7,
      "messages_sent": 420
    }
  ]
}`}
        />

        <EndpointCard
          method="GET"
          endpoint="/api/v1/reports/csat"
          description="Get Customer Satisfaction (CSAT) survey results and ratings"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
          ]}
          parameters={[
            { name: 'start_date', type: 'date', required: true, description: 'Start date (YYYY-MM-DD)' },
            { name: 'end_date', type: 'date', required: true, description: 'End date (YYYY-MM-DD)' },
          ]}
          requestExample={`curl -X GET "http://localhost:8001/api/v1/reports/csat?start_date=2025-11-01&end_date=2025-11-25" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`}
          responseExample={`{
  "total_responses": 120,
  "average_rating": 4.5,
  "ratings_breakdown": {
    "1": 5,
    "2": 8,
    "3": 15,
    "4": 42,
    "5": 50
  },
  "satisfaction_percentage": 90.0,
  "response_rate": 26.7
}`}
        />

        <EndpointCard
          method="GET"
          endpoint="/api/v1/reports/dashboard"
          description="Get real-time dashboard metrics overview"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
          ]}
          requestExample={`curl -X GET http://localhost:8001/api/v1/reports/dashboard \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`}
          responseExample={`{
  "online_agents": 12,
  "total_conversations": 450,
  "open_conversations": 70,
  "pending_conversations": 25,
  "unassigned_conversations": 8,
  "average_wait_time_minutes": 3.5,
  "avg_resolution_time_hours": 2.5
}`}
        />

        <EndpointCard
          method="GET"
          endpoint="/api/v1/advanced-reports/export"
          description="Export detailed reports in CSV or Excel format"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
          ]}
          parameters={[
            { name: 'report_type', type: 'string', required: true, description: 'Type: conversations, agents, csat' },
            { name: 'start_date', type: 'date', required: true, description: 'Start date (YYYY-MM-DD)' },
            { name: 'end_date', type: 'date', required: true, description: 'End date (YYYY-MM-DD)' },
            { name: 'format', type: 'string', required: false, description: 'Export format: csv, xlsx (default: csv)' },
          ]}
          requestExample={`curl -X GET "http://localhost:8001/api/v1/advanced-reports/export?report_type=conversations&start_date=2025-11-01&end_date=2025-11-25&format=xlsx" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  --output report.xlsx`}
          responseExample={`Binary file download (Excel/CSV format)`}
        />
      </TabPanel>

      <TabPanel value={selectedTab} index={7}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Inboxes & Channels API
        </Typography>
        <Typography variant="body1" paragraph>
          Manage communication channels including inboxes, widgets, email, WhatsApp, SMS, and social media integrations.
        </Typography>

        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
          Inboxes Management
        </Typography>

        <EndpointCard
          method="GET"
          endpoint="/api/v1/inboxes"
          description="Get list of all inboxes with configuration details"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
          ]}
          requestExample={`curl -X GET http://localhost:8001/api/v1/inboxes \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`}
          responseExample={`{
  "inboxes": [
    {
      "id": 1,
      "name": "Website Chat",
      "channel_type": "web_widget",
      "greeting_enabled": true,
      "greeting_message": "Welcome! How can we help?",
      "enable_auto_assignment": true,
      "working_hours_enabled": false,
      "out_of_office_message": null,
      "created_at": "2025-10-01T10:00:00Z"
    }
  ]
}`}
        />

        <EndpointCard
          method="POST"
          endpoint="/api/v1/inboxes"
          description="Create a new inbox"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
            { name: 'Content-Type', value: 'application/json', description: 'Request body format' },
          ]}
          parameters={[
            { name: 'name', type: 'string', required: true, description: 'Inbox name' },
            { name: 'channel_type', type: 'string', required: true, description: 'Type: web_widget, email, whatsapp, api, telegram, sms' },
            { name: 'greeting_enabled', type: 'boolean', required: false, description: 'Enable greeting message' },
            { name: 'greeting_message', type: 'string', required: false, description: 'Greeting message text' },
          ]}
          requestExample={`curl -X POST http://localhost:8001/api/v1/inboxes \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Support Chat",
    "channel_type": "web_widget",
    "greeting_enabled": true,
    "greeting_message": "Hello! How may I assist you?"
  }'`}
          responseExample={`{
  "id": 5,
  "name": "Support Chat",
  "channel_type": "web_widget",
  "greeting_enabled": true,
  "greeting_message": "Hello! How may I assist you?",
  "created_at": "2025-11-25T14:30:00Z"
}`}
        />

        <EndpointCard
          method="GET"
          endpoint="/api/v1/inboxes/{inbox_id}/stats"
          description="Get statistics for a specific inbox"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
          ]}
          requestExample={`curl -X GET http://localhost:8001/api/v1/inboxes/1/stats \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`}
          responseExample={`{
  "inbox_id": 1,
  "total_conversations": 250,
  "open_conversations": 35,
  "resolved_conversations": 215,
  "average_response_time_minutes": 6.8,
  "messages_today": 142
}`}
        />

        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 4 }}>
          Widget Configuration
        </Typography>

        <EndpointCard
          method="GET"
          endpoint="/api/v1/inboxes/{inbox_id}/widget-config"
          description="Get widget configuration for an inbox"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
          ]}
          requestExample={`curl -X GET http://localhost:8001/api/v1/inboxes/1/widget-config \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`}
          responseExample={`{
  "website_token": "abc123xyz789",
  "welcome_heading": "Welcome to Support",
  "welcome_tagline": "We're here to help!",
  "widget_color": "#1f93ff",
  "position": "right",
  "launcher_title": "Chat with us",
  "enable_unread_badge": true
}`}
        />

        <EndpointCard
          method="POST"
          endpoint="/api/v1/whatsapp/channels"
          description="Create a WhatsApp channel"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
            { name: 'Content-Type', value: 'application/json', description: 'Request body format' },
          ]}
          parameters={[
            { name: 'phone_number', type: 'string', required: true, description: 'WhatsApp phone number' },
            { name: 'provider', type: 'string', required: true, description: 'Provider: twilio, 360dialog' },
            { name: 'api_key', type: 'string', required: true, description: 'Provider API key' },
          ]}
          requestExample={`curl -X POST http://localhost:8001/api/v1/whatsapp/channels \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone_number": "+1234567890",
    "provider": "twilio",
    "api_key": "your_api_key_here"
  }'`}
          responseExample={`{
  "id": 10,
  "phone_number": "+1234567890",
  "provider": "twilio",
  "status": "active",
  "created_at": "2025-11-25T15:00:00Z"
}`}
        />

        <EndpointCard
          method="POST"
          endpoint="/api/v1/email-channels"
          description="Create an email channel for support inbox"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
            { name: 'Content-Type', value: 'application/json', description: 'Request body format' },
          ]}
          parameters={[
            { name: 'email', type: 'string', required: true, description: 'Support email address' },
            { name: 'imap_enabled', type: 'boolean', required: true, description: 'Enable IMAP for receiving emails' },
            { name: 'imap_address', type: 'string', required: false, description: 'IMAP server address' },
            { name: 'smtp_address', type: 'string', required: false, description: 'SMTP server for sending' },
          ]}
          requestExample={`curl -X POST http://localhost:8001/api/v1/email-channels \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "support@example.com",
    "imap_enabled": true,
    "imap_address": "imap.gmail.com",
    "smtp_address": "smtp.gmail.com"
  }'`}
          responseExample={`{
  "id": 15,
  "email": "support@example.com",
  "imap_enabled": true,
  "status": "active",
  "created_at": "2025-11-25T15:15:00Z"
}`}
        />
      </TabPanel>

      <TabPanel value={selectedTab} index={8}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Labels & Automation API
        </Typography>
        <Typography variant="body1" paragraph>
          Manage conversation labels, automated workflows, canned responses, and business rules.
        </Typography>

        <Typography variant="h6" gutterBottom fontWeight="bold">
          Labels
        </Typography>

        <EndpointCard
          method="GET"
          endpoint="/api/v1/labels"
          description="Get all available labels"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
          ]}
          requestExample={`curl -X GET http://localhost:8001/api/v1/labels \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`}
          responseExample={`{
  "labels": [
    {
      "id": 1,
      "title": "Bug",
      "description": "Technical issues",
      "color": "#ff0000",
      "show_on_sidebar": true,
      "created_at": "2025-10-15T10:00:00Z"
    },
    {
      "id": 2,
      "title": "Feature Request",
      "description": "New feature requests",
      "color": "#00ff00",
      "show_on_sidebar": true,
      "created_at": "2025-10-15T10:05:00Z"
    }
  ]
}`}
        />

        <EndpointCard
          method="POST"
          endpoint="/api/v1/labels"
          description="Create a new label"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
            { name: 'Content-Type', value: 'application/json', description: 'Request body format' },
          ]}
          parameters={[
            { name: 'title', type: 'string', required: true, description: 'Label name' },
            { name: 'description', type: 'string', required: false, description: 'Label description' },
            { name: 'color', type: 'string', required: true, description: 'Hex color code (e.g., #ff0000)' },
            { name: 'show_on_sidebar', type: 'boolean', required: false, description: 'Show in sidebar (default: true)' },
          ]}
          requestExample={`curl -X POST http://localhost:8001/api/v1/labels \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Urgent",
    "description": "High priority issues",
    "color": "#ff6b6b",
    "show_on_sidebar": true
  }'`}
          responseExample={`{
  "id": 15,
  "title": "Urgent",
  "description": "High priority issues",
  "color": "#ff6b6b",
  "show_on_sidebar": true,
  "created_at": "2025-11-25T16:00:00Z"
}`}
        />

        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 4 }}>
          Automation Rules
        </Typography>

        <EndpointCard
          method="GET"
          endpoint="/api/v1/automation/rules"
          description="Get all automation rules"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
          ]}
          requestExample={`curl -X GET http://localhost:8001/api/v1/automation/rules \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`}
          responseExample={`{
  "rules": [
    {
      "id": 1,
      "name": "Auto-assign to support team",
      "event_name": "conversation_created",
      "conditions": [
        {"attribute": "inbox_id", "operator": "equals", "value": "1"}
      ],
      "actions": [
        {"action_name": "assign_team", "action_params": {"team_id": 5}}
      ],
      "active": true,
      "created_at": "2025-10-20T12:00:00Z"
    }
  ]
}`}
        />

        <EndpointCard
          method="POST"
          endpoint="/api/v1/automation/rules"
          description="Create a new automation rule"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
            { name: 'Content-Type', value: 'application/json', description: 'Request body format' },
          ]}
          parameters={[
            { name: 'name', type: 'string', required: true, description: 'Rule name' },
            { name: 'event_name', type: 'string', required: true, description: 'Event: conversation_created, message_created' },
            { name: 'conditions', type: 'array', required: true, description: 'Array of condition objects' },
            { name: 'actions', type: 'array', required: true, description: 'Array of action objects' },
            { name: 'active', type: 'boolean', required: false, description: 'Enable rule (default: true)' },
          ]}
          requestExample={`curl -X POST http://localhost:8001/api/v1/automation/rules \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Auto-label VIP customers",
    "event_name": "conversation_created",
    "conditions": [
      {"attribute": "contact.custom_attributes.plan", "operator": "equals", "value": "enterprise"}
    ],
    "actions": [
      {"action_name": "add_label", "action_params": {"label_ids": [5]}}
    ],
    "active": true
  }'`}
          responseExample={`{
  "id": 10,
  "name": "Auto-label VIP customers",
  "event_name": "conversation_created",
  "active": true,
  "created_at": "2025-11-25T16:30:00Z"
}`}
        />

        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 4 }}>
          Canned Responses
        </Typography>

        <EndpointCard
          method="GET"
          endpoint="/api/v1/canned-responses"
          description="Get all canned responses (quick reply templates)"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
          ]}
          requestExample={`curl -X GET http://localhost:8001/api/v1/canned-responses \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`}
          responseExample={`{
  "canned_responses": [
    {
      "id": 1,
      "short_code": "greeting",
      "content": "Hello! Thank you for contacting us. How can I help you today?",
      "created_at": "2025-10-10T09:00:00Z"
    }
  ]
}`}
        />

        <EndpointCard
          method="POST"
          endpoint="/api/v1/canned-responses"
          description="Create a new canned response template"
          headers={[
            { name: 'Authorization', value: 'Bearer YOUR_ACCESS_TOKEN', description: 'JWT access token' },
            { name: 'Content-Type', value: 'application/json', description: 'Request body format' },
          ]}
          parameters={[
            { name: 'short_code', type: 'string', required: true, description: 'Shortcut code for quick access' },
            { name: 'content', type: 'string', required: true, description: 'Template message content' },
          ]}
          requestExample={`curl -X POST http://localhost:8001/api/v1/canned-responses \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "short_code": "followup",
    "content": "I wanted to follow up on your previous inquiry. Have you had a chance to review the information I provided?"
  }'`}
          responseExample={`{
  "id": 25,
  "short_code": "followup",
  "content": "I wanted to follow up on your previous inquiry. Have you had a chance to review the information I provided?",
  "created_at": "2025-11-25T17:00:00Z"
}`}
        />
      </TabPanel>

      <TabPanel value={selectedTab} index={9}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          SDKs & Client Libraries
        </Typography>
        <Typography variant="body1" paragraph>
          Official SDKs and client libraries for popular programming languages.
        </Typography>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight="bold">JavaScript / TypeScript SDK</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Install via npm:
            </Typography>
            <CodeBlock code="npm install @converse/js-sdk" language="bash" />
            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
              Quick Start:
            </Typography>
            <CodeBlock
              code={`import { ConverseClient } from '@converse/js-sdk';

const client = new ConverseClient({
  apiKey: 'YOUR_API_KEY',
  baseUrl: 'https://api.converse.ai/v1'
});

// Send a message
const message = await client.conversations.sendMessage(1, {
  content: 'Hello from SDK!'
});

// Listen to real-time events
client.on('message.created', (data) => {
  console.log('New message:', data);
});`}
              language="typescript"
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight="bold">Python SDK</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Install via pip:
            </Typography>
            <CodeBlock code="pip install converse-python-sdk" language="bash" />
            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
              Quick Start:
            </Typography>
            <CodeBlock
              code={`from converse import ConverseClient

client = ConverseClient(
    api_key='YOUR_API_KEY',
    base_url='https://api.converse.ai/v1'
)

# Get conversations
conversations = client.conversations.list(status='open')

# Send a message
message = client.conversations.send_message(
    conversation_id=1,
    content='Hello from Python SDK!'
)`}
              language="python"
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight="bold">Ruby SDK</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Install via gem:
            </Typography>
            <CodeBlock code="gem install converse-ruby" language="bash" />
            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
              Quick Start:
            </Typography>
            <CodeBlock
              code={`require 'converse'

client = Converse::Client.new(
  api_key: 'YOUR_API_KEY',
  base_url: 'https://api.converse.ai/v1'
)

# Create a contact
contact = client.contacts.create(
  name: 'John Doe',
  email: 'john@example.com'
)`}
              language="ruby"
            />
          </AccordionDetails>
        </Accordion>

        <Alert severity="info" sx={{ mt: 3 }}>
          More SDKs coming soon: PHP, Go, Java, C#. Check our GitHub repository for the latest updates.
        </Alert>
      </TabPanel>

      {/* Rate Limits */}
      <Paper sx={{ p: 3, mt: 4, bgcolor: '#f5f5f5' }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
          Rate Limits
        </Typography>
        <Typography variant="body2" paragraph>
          API requests are rate-limited to ensure fair usage and system stability.
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Plan</strong></TableCell>
                <TableCell><strong>Requests per Minute</strong></TableCell>
                <TableCell><strong>Requests per Hour</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Free</TableCell>
                <TableCell>60</TableCell>
                <TableCell>1,000</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Pro</TableCell>
                <TableCell>300</TableCell>
                <TableCell>10,000</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Enterprise</TableCell>
                <TableCell>1,000</TableCell>
                <TableCell>50,000</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Alert severity="warning" sx={{ mt: 2 }}>
          When rate limit is exceeded, you'll receive a <code>429 Too Many Requests</code> response with a <code>Retry-After</code> header.
        </Alert>
      </Paper>

      {/* Support */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: '#e3f2fd' }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Need Help?
        </Typography>
        <Typography variant="body1">
          For API support, integration questions, or to report issues:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="📧 Email: api-support@converse.ai"
              secondary="Response within 24 hours"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="💬 Developer Slack: converse-dev.slack.com"
              secondary="Join our developer community"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="📚 GitHub: github.com/converse-ai/api-examples"
              secondary="Sample code and integration examples"
            />
          </ListItem>
        </List>
      </Paper>
    </Container>
  );
};

export default APIDocumentationPage;
